import { useCallback, useEffect, useState } from 'react'
import { Footer } from '@/components/layout/Footer'
import { Header } from '@/components/layout/Header'
import { CursorGlow } from '@/components/motion/CursorGlow'
import { GrainOverlay } from '@/components/motion/GrainOverlay'
import { ScrollProgress } from '@/components/motion/ScrollProgress'
import { DocumentHead } from '@/components/seo/DocumentHead'
import { HomePage } from '@/pages/HomePage'
import { LegalPage } from '@/pages/LegalPage'
import { PolicyPage } from '@/pages/PolicyPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { PrivacyPage } from '@/pages/PrivacyPage'
import { SiteProvider, useTranslations } from '@/app/site-context'
import type { Route } from '@/app/routes'
import { matchRoute } from '@/app/routes'
import { remeasureScroll } from '@/lib/scroll-store'

const PRIVACY_RELATED = ['mediusDeletion', 'mediusTerms'] as const
const DELETION_RELATED = ['mediusPrivacy', 'mediusTerms'] as const
const TERMS_RELATED = ['mediusPrivacy', 'mediusDeletion'] as const
const SPARSA_PRIVACY_RELATED = ['sparsaDeletion', 'sparsaTerms', 'sparsaAcceptableUse'] as const
const SPARSA_DELETION_RELATED = ['sparsaPrivacy', 'sparsaTerms'] as const
const SPARSA_TERMS_RELATED = ['sparsaAcceptableUse', 'sparsaPrivacy', 'sparsaDeletion'] as const
const SPARSA_ACCEPTABLE_USE_RELATED = ['sparsaTerms', 'sparsaPrivacy'] as const

interface AppProps {
  route: Route
}

function Shell({ route, onNavigate }: { route: Route; onNavigate: (href: string) => void }) {
  const t = useTranslations()

  return (
    <>
      <DocumentHead />
      <a className="skip-link" href="#main">
        {t.a11y.skipToContent}
      </a>
      <GrainOverlay />
      <ScrollProgress />
      <CursorGlow />
      <Header onNavigate={onNavigate} />
      <main id="main">
        {route.kind === 'home' ? <HomePage onNavigate={onNavigate} /> : null}
        {route.kind === 'privacy' ? <PrivacyPage onNavigate={onNavigate} /> : null}
        {route.kind === 'legal' ? <LegalPage onNavigate={onNavigate} /> : null}
        {route.kind === 'mediusPrivacy' ? (
          <PolicyPage kind="mediusPrivacy" related={PRIVACY_RELATED} onNavigate={onNavigate} />
        ) : null}
        {route.kind === 'mediusDeletion' ? (
          <PolicyPage kind="mediusDeletion" related={DELETION_RELATED} onNavigate={onNavigate} />
        ) : null}
        {route.kind === 'mediusTerms' ? (
          <PolicyPage kind="mediusTerms" related={TERMS_RELATED} onNavigate={onNavigate} />
        ) : null}
        {route.kind === 'sparsaPrivacy' ? (
          <PolicyPage
            kind="sparsaPrivacy"
            related={SPARSA_PRIVACY_RELATED}
            onNavigate={onNavigate}
          />
        ) : null}
        {route.kind === 'sparsaDeletion' ? (
          <PolicyPage
            kind="sparsaDeletion"
            related={SPARSA_DELETION_RELATED}
            onNavigate={onNavigate}
          />
        ) : null}
        {route.kind === 'sparsaTerms' ? (
          <PolicyPage kind="sparsaTerms" related={SPARSA_TERMS_RELATED} onNavigate={onNavigate} />
        ) : null}
        {route.kind === 'sparsaAcceptableUse' ? (
          <PolicyPage
            kind="sparsaAcceptableUse"
            related={SPARSA_ACCEPTABLE_USE_RELATED}
            onNavigate={onNavigate}
          />
        ) : null}
        {route.kind === 'notFound' ? <NotFoundPage onNavigate={onNavigate} /> : null}
      </main>
      <Footer onNavigate={onNavigate} />
    </>
  )
}

export function App({ route: initialRoute }: AppProps) {
  const [route, setRoute] = useState(initialRoute)

  const navigate = useCallback((href: string) => {
    const [pathname = '/', hash] = href.split('#')
    window.history.pushState({}, '', href)
    setRoute(matchRoute(pathname))

    if (hash === undefined) {
      window.scrollTo({ top: 0, behavior: 'auto' })
      return
    }

    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
  }, [])

  useEffect(() => {
    const actual = matchRoute(window.location.pathname)
    setRoute((current) =>
      current.kind === actual.kind && current.locale === actual.locale ? current : actual,
    )
  }, [])

  useEffect(() => {
    const onPopState = () => {
      setRoute(matchRoute(window.location.pathname))
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  useEffect(() => {
    remeasureScroll()
  }, [route])

  return (
    <SiteProvider route={route}>
      <Shell route={route} onNavigate={navigate} />
    </SiteProvider>
  )
}
